import * as Dialog from '@radix-ui/react-dialog';
import { ExternalLink, X } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ArticleView } from './article-view';

interface HelpPanelApi {
  open(slug: string, section?: string): void;
  slug: string | null;
}
const Ctx = createContext<HelpPanelApi | null>(null);
export const useHelpPanel = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error('useHelpPanel needs <HelpPanelProvider>');
  return c;
};

/** Onboarding as a side panel next to the current screen (PLAN §12.6). */
export function HelpPanelProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<{ slug: string; section?: string } | null>(null);
  const open = useCallback((slug: string, section?: string) => setTarget({ slug, section }), []);
  const api = useMemo(() => ({ open, slug: target?.slug ?? null }), [open, target]);

  useEffect(() => {
    if (!target?.section) return;
    const t = setTimeout(() => document.getElementById(target.section as string)?.scrollIntoView({ block: 'start' }), 50);
    return () => clearTimeout(t);
  }, [target]);

  return (
    <Ctx.Provider value={api}>
      {children}
      <Dialog.Root open={!!target} onOpenChange={o => !o && setTarget(null)}>
        <Dialog.Portal>
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col border-s bg-surface-raised shadow-xl"
            onInteractOutside={e => e.preventDefault()}
          >
            <div className="flex items-center justify-between border-b px-4 py-2">
              <Dialog.Title className="text-sm font-semibold">Help</Dialog.Title>
              <div className="flex items-center gap-1">
                {target && (
                  <a href={`/onboarding/${target.slug}`} className="flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-muted">
                    Open full page <ExternalLink className="size-3" aria-hidden="true" />
                  </a>
                )}
                <Dialog.Close aria-label="Close help" className="rounded p-1 text-muted-foreground hover:text-foreground">
                  <X className="size-4" aria-hidden="true" />
                </Dialog.Close>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {target && <ArticleView slug={target.slug} compact onNavigate={(slug, section) => setTarget({ slug, section })} />}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Ctx.Provider>
  );
}
